import { Button, IconButton } from '@otter-ai/den-components';
import { Trash_2Icon, ZapierIcon } from '@otter-ai/den-icons';
import type { DeactivateApiKeyResponse } from '@otter-ai/recall-client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { SETTINGS_PATHS } from '@/constants/path-constants';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { FormattedText } from '@/shared/components/FormattedText';
import { openDialog } from '@/shared/modals/openDialog';
import { descriptionFormatRules } from '../../shared/utils/text';
import { AppRowLayout } from '../components/AppRowLayout';
import { useZapierApiKeys } from '../hooks/useZapierApiKey';
import { trackZapier } from '../tracking/zapier-tracking';

export function ZapierAppRow() {
  const t = useTranslations('settings:integrations.apps.zapier');
  const isPublicApiEnabled = useExperimentEnabled('public_api');
  const { hasApiKey, partialValue, deactivateApiKeyAsync, apiKeyId } =
    useZapierApiKeys();

  const extendContent =
    !isPublicApiEnabled && hasApiKey && apiKeyId ? (
      <ShowApiKey
        partialValue={partialValue}
        deactivateApiKeyAsync={deactivateApiKeyAsync}
        apiKeyId={apiKeyId}
      />
    ) : null;

  return (
    <AppRowLayout
      appRowId="zapier-app"
      data-testid="zapier-app-row"
      Icon={ZapierIcon}
      title="Zapier"
      description={
        <FormattedText>
          {t.rich(
            'description',
            descriptionFormatRules({
              links: {
                'help-link': {
                  href: t('helpUrl'),
                  target: '_blank',
                  onClick: () => trackZapier.main.clickHelpCenter(),
                },
              },
            }),
          )}
        </FormattedText>
      }
      extendContent={extendContent}
      buttons={
        <div className="flex flex-row items-stretch gap-2">
          <Button
            variant="outlineNeutral"
            version="legacy"
            data-testid="create-zap-button"
            onClick={() => {
              trackZapier.main.clickCreateZap();
              window.open(
                'https://zapier.com/apps/otterai/integrations',
                '_blank',
              );
            }}
          >
            {t('createAZapButton')}
          </Button>
          {!isPublicApiEnabled && !hasApiKey && (
            <Button
              data-testid="generate-zapier-api-key-button"
              onClick={() => {
                trackZapier.main.clickApiKey();
                openGenerateKeyModal();
              }}
            >
              {t('apiKeyButton')}
            </Button>
          )}
          {isPublicApiEnabled && (
            <Button
              asChild
              data-testid="zapier-api-key-button"
              onClick={() => trackZapier.main.clickApiKey()}
            >
              <Link href={SETTINGS_PATHS.SETTINGS_DEVELOPER}>
                {t('apiKeyButton')}
              </Link>
            </Button>
          )}
        </div>
      }
      prefetch={
        !isPublicApiEnabled
          ? () => {
              import('./ZapierGenerateKeyModal');
              import('./ZapierDeleteKeyModal');
            }
          : undefined
      }
    />
  );
}

function ShowApiKey({
  partialValue,
  apiKeyId,
  deactivateApiKeyAsync,
}: {
  partialValue: string | null;
  deactivateApiKeyAsync: (
    apiKey: number | string,
  ) => Promise<DeactivateApiKeyResponse>;
  apiKeyId: string | number;
}) {
  const onDeleteClick = async () => {
    const doDelete = await openDialog<boolean>(async ({ done }) => {
      const { ZapierDeleteKeyModal } = await import('./ZapierDeleteKeyModal');
      return <ZapierDeleteKeyModal done={done} partialValue={partialValue} />;
    });

    // By testing that it is not true, we don't accidentally delete the key
    // if doDelete is something unexpected.
    if (doDelete !== true) {
      return;
    }
    await deactivateApiKeyAsync(apiKeyId);
  };

  const t = useTranslations('settings:integrations.apps.zapier');
  return (
    <div className="pt-6 flex flex-row gap-2 items-center">
      <div data-testid="api-key-value">
        ****************************{partialValue}
      </div>
      <IconButton
        variant="ghost"
        data-testid="delete-api-key"
        onClick={() => {
          trackZapier.main.clickDeleteKey();
          onDeleteClick();
        }}
      >
        <Trash_2Icon size={24} aria-label={t('deleteKeyButton')} />
      </IconButton>
    </div>
  );
}

function openGenerateKeyModal() {
  trackZapier.generateDialog.openDialog();
  openDialog(async ({ done }) => {
    const { ZapierGenerateKeyModal } = await import('./ZapierGenerateKeyModal');
    return <ZapierGenerateKeyModal done={done} />;
  });
}
